/*
 * Copyright (c) 2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

export enum MeterProductCode {
  AGS = 'ags_starter',
  AMS = 'ams',
  EXTEND = 'extend'
}

export enum PaymentStatus {
  READY = 'ready',
  PAID = 'paid',
  COMPLETED = 'completed',
  PAST_DUE = 'past_due'
}

const PAYMENT_STATUS_TO_NAME: Record<PaymentStatus, string> = {
  [PaymentStatus.READY]: 'Ready',
  [PaymentStatus.PAID]: 'Paid',
  [PaymentStatus.COMPLETED]: 'Completed',
  [PaymentStatus.PAST_DUE]: 'Past Due'
}

export const MAX_FRACTION_DIGITS: Record<MeterProductCode, number> = {
  [MeterProductCode.AGS]: 4,
  [MeterProductCode.AMS]: 3,
  [MeterProductCode.EXTEND]: 3
}

const PAYMENT_STATUS_TAG: Record<PaymentStatus, { text: string; className?: string }> = {
  [PaymentStatus.READY]: {
    text: PAYMENT_STATUS_TO_NAME[PaymentStatus.READY],
    className: 'bg-common-fillAlter border-common-border text-common-text'
  },
  [PaymentStatus.PAID]: { text: PAYMENT_STATUS_TO_NAME[PaymentStatus.PAID], className: 'bg-blue-1 text-blue-7 border-blue-3' },
  [PaymentStatus.COMPLETED]: {
    text: PAYMENT_STATUS_TO_NAME[PaymentStatus.COMPLETED],
    className: 'border-green-3 bg-green-1 text-green-8'
  },
  [PaymentStatus.PAST_DUE]: { text: PAYMENT_STATUS_TO_NAME[PaymentStatus.PAST_DUE], className: 'bg-red-1 text-red-7 border-red-3' }
}

export class BillingHistoryHelpers {
  static getPaymentStatusInfo = (status?: string) =>
    status ? PAYMENT_STATUS_TAG[status as PaymentStatus] : { text: 'Unidentified payment status' }

  static getBillingAmount = (amount: string | number, options?: { locale?: string; currency?: string; maximumFractionDigits?: number }) => {
    const parsedAmount = typeof amount === 'string' ? convertPaddlePriceToNumber(amount) : amount

    return new Intl.NumberFormat(options?.locale ?? 'en-US', {
      style: 'currency',
      currency: options?.currency ?? 'USD',
      maximumFractionDigits: options?.maximumFractionDigits ?? 2
    }).format(parsedAmount)
  }
}

function convertPaddlePriceToNumber(amount: string): number {
  // In Paddle, the amount is of type string. If the price is "1", then the value is "100".
  // This is because they allow 2 decimals behind 0s. So, what we need to do is, we "snuck" a dot
  // before the last 2 characters, then parse it to number.
  const newAmount = amount.slice(0, -2) + '.' + amount.slice(-2)
  return Number(newAmount)
}
